import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import AnimatedRoutes from "./AnimatedRoutes";
import "./App.css";

// Create a separate component for the preloader to use hooks
function GlobalPreloader({ children, lang, setLang }) {
  // Skip the loading animation when react-snap is pre-rendering (headless Chrome),
  // or when hydrating a pre-rendered page (root already has DOM children).
  // This ensures react-snap captures a fully visible page, and that normal users
  // hydrating that snapshot don't see a flash of invisible content.
  const isPrerender =
    typeof navigator !== "undefined" &&
    /HeadlessChrome/.test(navigator.userAgent);
  const isHydrating =
    typeof document !== "undefined" &&
    (document.getElementById("root")?.children?.length ?? 0) > 0;
  const skipPreloader = isPrerender || isHydrating;

  const [isLoading, setIsLoading] = useState(!skipPreloader);
  const [pageLoaded, setPageLoaded] = useState(skipPreloader);
  const [showLoader, setShowLoader] = useState(!skipPreloader);
  const [animationsReady, setAnimationsReady] = useState(false); // Track hero animations
  const location = useLocation();
  const [prevLocation, setPrevLocation] = useState(location);

  // Function to preload critical assets - moved outside with useCallback
  const preloadCriticalAssets = useCallback(() => {
    return new Promise((resolve) => {
      const criticalAssets = [
        // Add self-hosted woff2 fonts here if needed, e.g. '/fonts/GeistMono.woff2'
        // Do NOT add src/assets images — CRA hashes them to /static/media/…
      ];

      let loadedCount = 0;

      if (criticalAssets.length === 0) {
        resolve();
        return;
      }

      const updateProgress = (loaded, total) => {
        const progress = (loaded / total) * 100;
        const progressBar = document.querySelector(".progress-fill");
        if (progressBar) {
          progressBar.style.width = `${progress}%`;
        }
      };

      criticalAssets.forEach((asset) => {
        const link = document.createElement("link");
        link.rel = "preload";

        if (asset.endsWith(".json")) {
          link.as = "fetch";
          link.type = "application/json";
          link.crossOrigin = "anonymous"; // Important for Lottie files
        } else if (asset.endsWith(".woff2") || asset.endsWith(".woff")) {
          link.as = "font";
          link.type = asset.endsWith(".woff2") ? "font/woff2" : "font/woff";
          link.crossOrigin = "anonymous";
        } else if (
          asset.endsWith(".png") ||
          asset.endsWith(".jpg") ||
          asset.endsWith(".webp")
        ) {
          link.as = "image";
        }

        link.href = asset;
        link.onload = () => {
          loadedCount++;
          updateProgress(loadedCount, criticalAssets.length);
          if (loadedCount === criticalAssets.length) {
            resolve();
          }
        };
        link.onerror = () => {
          loadedCount++;
          updateProgress(loadedCount, criticalAssets.length);
          if (loadedCount === criticalAssets.length) {
            resolve();
          }
        };
        document.head.appendChild(link);
      });
    });
  }, []);

  // Listen for hero animation readiness
  useEffect(() => {
    const handleAnimationReady = () => {
      setAnimationsReady(true);
    };

    // Listen for custom event from hero animations
    window.addEventListener("heroAnimationsReady", handleAnimationReady);

    return () => {
      window.removeEventListener("heroAnimationsReady", handleAnimationReady);
    };
  }, []);

  useEffect(() => {
    if (skipPreloader) {
      document.body.classList.add("animations-ready");
      document.body.style.overflow = "";  // clear any inline overflow:hidden from pre-render
      return;
    }
    if (isLoading) {
      const loadInitialAssets = async () => {
        await preloadCriticalAssets();

        // Failsafe: force loader to hide after 2 seconds if animations never signal
        let failsafeFired = false;
        const initialFailsafe = setTimeout(() => {
          failsafeFired = true;
          setIsLoading(false);
          setPageLoaded(true);
          document.body.classList.add("animations-ready");
          setShowLoader(false);
        }, 2000);

        // Wrap check so it cancels the failsafe on success
        const checkAndClear = () => {
          if (failsafeFired) return;
          if (
            animationsReady ||
            document.body.classList.contains("hero-animations-loaded")
          ) {
            clearTimeout(initialFailsafe);
            setIsLoading(false);
            setPageLoaded(true);
            document.body.classList.add("animations-ready");
            setTimeout(() => setShowLoader(false), 500);
          } else {
            setTimeout(checkAndClear, 100);
          }
        };
        checkAndClear();
      };

      loadInitialAssets();
    }

    // Handle route changes
    if (location.pathname !== prevLocation.pathname) {
      setShowLoader(true); // Show loader
      setIsLoading(true);
      setAnimationsReady(false); // Reset animation readiness

      const timeoutId = setTimeout(() => {
        // For route changes, use a shorter timeout but still check animations
        const checkRouteAnimationsReady = () => {
          if (
            animationsReady ||
            document.body.classList.contains("hero-animations-loaded")
          ) {
            setIsLoading(false);
            setPrevLocation(location);
            setTimeout(() => setShowLoader(false), 500);
          } else {
            setTimeout(checkRouteAnimationsReady, 100);
          }
        };

        // Failsafe: Force loader to hide after maximum 3 seconds
        const failsafeTimeout = setTimeout(() => {
          console.warn("Animation loading timeout - forcing loader to hide");
          setIsLoading(false);
          setPrevLocation(location);
          setShowLoader(false);
        }, 3000);

        setTimeout(() => {
          checkRouteAnimationsReady();
          clearTimeout(failsafeTimeout);
        }, 500);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    location,
    prevLocation,
    isLoading,
    skipPreloader,
    preloadCriticalAssets,
    animationsReady,
  ]);

  return (
    <>
      <div className={`global-preloader ${showLoader ? "visible" : "hidden"}`}>
        <div className="preloader-content">
          {/* Replace the loading-spinner with stars animation */}
          <div className="sparkling-stars-animation">
            <div className="sparkling-star  sparkling-star-1 shining-star"></div>
            <div className="sparkling-star  sparkling-star-2 shining-star"></div>
            <div className="sparkling-star  sparkling-star-3 shining-star"></div>
            <div className="stsparkling-star ar sparkling-star-4 shining-star"></div>
            <div className="sparkling-star  sparkling-star-5 shining-star"></div>
            <div className="sparkling-star  sparkling-star-6 shining-star"></div>
          </div>
        </div>
      </div>
      <div className={`content-wrapper ${pageLoaded ? "loaded" : ""}`}>
        <AnimatedRoutes lang={lang} setLang={setLang} />
      </div>
    </>
  );
}

const META_DESCRIPTIONS = {
  EN: "Yoru Solutions is a digital studio specializing in web development, product design, copywriting, and translation. We build fast, scalable, and visually compelling digital experiences for brands worldwide.",
  FR: "Yoru Solutions est un studio digital spécialisé en développement web, design produit, copywriting et traduction. Nous créons des expériences numériques rapides, évolutives et visuellement percutantes pour des marques du monde entier.",
};

// Main App Component
function App() {
  const [lang, setLang] = useState(() => {
    if (typeof navigator === "undefined" || /HeadlessChrome/.test(navigator.userAgent)) return "EN";
    return navigator.language.toLowerCase().startsWith("fr") ? "FR" : "EN";
  });

  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", META_DESCRIPTIONS[lang]);
  }, [lang]);

  return (
    <Router>
      <div className="app-layout">
        <GlobalPreloader lang={lang} setLang={setLang} />
      </div>
    </Router>
  );
}

export default App;
